/**
 * EntitySearchAutocomplete Component
 * Universal search for quotes, customers, invoices, orders, and leads
 */

import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Description,
  Person,
  Receipt,
  LocalShipping,
  Assignment,
} from "@mui/icons-material";
import { useEntitySearch } from "../hooks/useFollowUps.js";
import EntitySuggestionCard from "./EntitySuggestionCard.jsx";

/**
 * EntitySearchAutocomplete Component
 * @param {Object} props
 * @param {Object|null} props.value - Selected entity object
 * @param {Function} props.onChange - Callback when entity is selected
 * @param {string} [props.label] - Label for the field
 * @param {boolean} [props.disabled] - Whether field is disabled
 */
const EntitySearchAutocomplete = ({
  value,
  onChange,
  label = "Search Quote, Customer, Invoice, Order, or Lead",
  disabled = false,
  ...otherProps
}) => {
  // Ensure disabled is always a boolean
  const isDisabled = Boolean(disabled);
  const [inputValue, setInputValue] = useState("");
  const { data: options = [], isLoading: loading, error } = useEntitySearch(inputValue);

  // Debug logging
  useEffect(() => {
    if (inputValue) {
      console.log("🔍 [AUTOCOMPLETE] Input value changed:", inputValue, "Length:", inputValue.length);
    }
  }, [inputValue]);

  useEffect(() => {
    if (options.length > 0) {
      console.log("✅ [AUTOCOMPLETE] Options received:", options.length);
    }
    if (error) {
      console.error("❌ [AUTOCOMPLETE] Error:", error);
    }
  }, [options, error]);

  // Only show loading if we have at least 2 characters and are actually searching
  const isSearching = loading && inputValue.trim().length >= 2;

  // Get icon for entity type
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "quote":
        return <Description fontSize="small" />;
      case "customer":
        return <Person fontSize="small" />;
      case "invoice":
        return <Receipt fontSize="small" />;
      case "order":
        return <LocalShipping fontSize="small" />;
      case "lead":
        return <Assignment fontSize="small" />;
      default:
        return <Description fontSize="small" />;
    }
  };

  // Get color for entity type
  const getEntityColor = (entityType) => {
    switch (entityType) {
      case "quote":
        return "primary";
      case "customer":
        return "success";
      case "invoice":
        return "warning";
      case "order":
        return "info";
      case "lead":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <Autocomplete
      disabled={isDisabled}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      getOptionLabel={(option) => {
        if (!option) return "";
        return option.displayName || option.entityId || "Unknown";
      }}
      isOptionEqualToValue={(option, value) => {
        return (
          option?.entityId === value?.entityId &&
          option?.entityType === value?.entityType
        );
      }}
      loading={isSearching}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by name, phone, ID, or reference number..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearching ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={!!error}
          helperText={error ? "Search failed. Please try again." : ""}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            <Box sx={{ color: `${getEntityColor(option.entityType)}.main` }}>
              {getEntityIcon(option.entityType)}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography variant="body2" fontWeight="medium">
                  {option.displayName}
                </Typography>
                <Chip
                  label={option.entityType}
                  size="small"
                  color={getEntityColor(option.entityType)}
                  sx={{ height: 20, fontSize: "0.7rem", textTransform: "capitalize" }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {option.subtitle}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      noOptionsText={
        inputValue.trim().length < 2
          ? "Type at least 2 characters to search"
          : "No results found"
      }
    />
  );
};

export default EntitySearchAutocomplete;

